import React, { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import "./AboutUs.css";

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

const CollapsibleSection: React.FC<SectionProps> = ({ title, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="collapsible-section">
      <button
        className="section-header"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        {title}
        <span className={`arrow ${isOpen ? "open" : ""}`} aria-hidden="true">
          ▼
        </span>
      </button>
      <motion.div
        className="section-content"
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.5, ease: "easeInOut" }}
      >
        {isOpen && <div className="content-inner">{children}</div>}
      </motion.div>
    </div>
  );
};

const AnimatedSection: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.section
      ref={ref}
      className="about-section"
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      {children}
    </motion.section>
  );
};

const AboutUs: React.FC = () => {
  return (
    <main className="about-container" role="main" aria-label="About Hasskel">
      <h1 className="about-title">About Haaskel</h1>

      <AnimatedSection>
        <CollapsibleSection title="Our Mission">
          <p>
            At Haaskel, we believe in transforming ideas into stunning digital experiences.
            Our mission is to empower businesses and creatives with innovative web solutions
            that are visually captivating and functionally robust.
          </p>
          <p>
            From startups dreaming big to enterprises needing scalable digital infrastructure,
            our team crafts custom websites, apps, and brand identities tailored specifically
            to your goals and audience.
          </p>
        </CollapsibleSection>
      </AnimatedSection>

      <AnimatedSection>
        <CollapsibleSection title="What We Do">
          <ul>
            <li><strong>Custom Website Development:</strong> Luxury, performant, accessible websites.</li>
            <li><strong>UI/UX Design:</strong> Intuitive interfaces and delightful user journeys.</li>
            <li><strong>Brand Strategy & Identity:</strong> Memorable logos, colors, and messaging.</li>
            <li><strong>E-Commerce Platforms:</strong> Integrated shopping experiences with secure payments.</li>
            <li><strong>SEO & Performance Optimization:</strong> Rank higher and load faster.</li>
            <li><strong>Consulting & Strategy:</strong> Personalized digital transformation guidance.</li>
          </ul>
        </CollapsibleSection>
      </AnimatedSection>

      <AnimatedSection>
        <CollapsibleSection title="Our Process">
          <p>
            Collaboration is at the heart of everything we do. We listen carefully to your needs,
            then ideate and prototype with continuous feedback.
          </p>
          <p>
            Agile methods allow us to deliver iteratively with transparency, rigorous testing,
            optimization, and strong launch support.
          </p>
        </CollapsibleSection>
      </AnimatedSection>

      <AnimatedSection>
        <CollapsibleSection title="Meet The Team">
          <p>
            Our diverse team brings decades of experience from startups to Fortune 500 companies.
            Passionate about innovation, we stay at the cutting edge of tech and trends.
          </p>
        </CollapsibleSection>
      </AnimatedSection>

      <AnimatedSection>
        <CollapsibleSection title="Why Choose Haaskel?">
          <p>
            Because we build not just websites but lasting relationships. Your success is ours.
            Join us and craft something remarkable.
          </p>
          <p>Contact us today to start your project!</p>
        </CollapsibleSection>
      </AnimatedSection>
    </main>
  );
};

export default AboutUs;
